/**
 * ═══════════════════════════════════════════════
 * Custom Slide Cart — The Black Stuff
 * ═══════════════════════════════════════════════
 * 
 * Zero-dependency slide cart using Shopify's AJAX Cart API.
 * Handles: open/close, quantity changes, remove, upsells,
 * shipping progress, discount codes, cart notes, and 
 * integration with add-to-cart forms/buttons.
 */

(function () {
  'use strict';

  /* ── Config ──────────────────────────────────── */
  const CONFIG = window.slideCartConfig || {};
  const ROUTES = {
    cart: '/cart.js',
    add: '/cart/add.js',
    change: '/cart/change.js',
    update: '/cart/update.js',
    clear: '/cart/clear.js',
  };

  /* ── State ───────────────────────────────────── */
  let cart = CONFIG.cart || { items: [], item_count: 0, total_price: 0 };
  let isOpen = false;
  let isUpdating = false;
  let upsellProductIds = [];
  let debounceTimers = {};

  /* ── Compare-at prices ────────────────────────
     Shopify's AJAX cart API does not return compare_at_price on line items,
     so the drawer can't read it from cart.js. This map is seeded server-side
     for items already in the cart, then topped up lazily from
     /products/{handle}.js as new items get added. */
  const compareAtCache = Object.assign({}, CONFIG.compareAt || {});
  const compareAtPending = new Set();   // handles currently being fetched
  const compareAtMissing = new Set();   // handles confirmed to have none

  /* ── Packaging lines (EDP boxes) ──────────────
     The atomiser boxes are empty packaging that only ships as part of an
     EDP bundle. The bundle decides how many go in the cart; the drawer must
     never let a customer raise that number, and must never offer a box as
     an upsell. Decreasing or removing is still allowed.
     Matched by variant ID, product ID or handle, so a renamed product still
     gets caught. Override from Liquid via slideCartConfig if the list grows. */
  const PACKAGING_VARIANT_IDS = new Set(
    (CONFIG.packagingVariantIds || [50680574738758, 50682298368326, 53638910705990]).map(Number)
  );
  const PACKAGING_PRODUCT_IDS = new Set(
    (CONFIG.packagingProductIds || [9318567182662, 9318914130246, 14778232832326]).map(Number)
  );
  const PACKAGING_HANDLES = new Set(
    CONFIG.packagingHandles || ['edp-3-pack-box', 'edp-5-pack-box', 'single-pack']
  );
  const PACKAGING_MSG = CONFIG.packagingMessage ||
    'This box comes with your EDP bundle, so you can\'t add more of it here.';

  /* Fixed quantity readout shown in place of the stepper on packaging lines,
     matching the wording on the /cart page. */
  function packagingQtyHtml(qty) {
    return `<div class="sc-item__pack-qty" data-sc-pack-qty>
      <span class="sc-item__pack-qty-num">Quantity: ${Number(qty) || 0}</span>
      <span class="sc-item__pack-qty-note">Comes with your EDP bundle</span>
    </div>`;
  }

  function isPackagingLine(item) {
    if (!item) return false;
    return PACKAGING_VARIANT_IDS.has(Number(item.variant_id)) ||
      PACKAGING_PRODUCT_IDS.has(Number(item.product_id)) ||
      PACKAGING_HANDLES.has(item.handle);
  }

  function isPackagingProduct(p) {
    if (!p) return false;
    return PACKAGING_PRODUCT_IDS.has(Number(p.id)) || PACKAGING_HANDLES.has(p.handle);
  }

  function getLineByKey(key) {
    return (cart.items || []).find(i => i.key === key) || null;
  }

  /* ── Orphaned boxes ──────────────────────────
     A box with no EDPs beside it is an empty box. If the customer takes the
     fragrances out, the box goes too. An EDP vial is matched by its SKU
     (every single vial starts EDP-10-), so ready-made EDP sets and the boxes
     themselves don't count as fragrances here.
     Only runs on customer actions (remove, quantity change, checkout, /cart
     page load), never on a plain cart refresh, so it can't strip a box out
     halfway through the bundle builder adding its items. */
  const ORPHAN_MSG = CONFIG.orphanPackagingMessage ||
    'We\'ve taken the EDP box out too, as it only comes with the EDPs.';
  const EDP_SKU_PREFIX = (CONFIG.edpSkuPrefix || 'EDP-10-').toUpperCase();

  function isEdpVialLine(item) {
    if (!item || isPackagingLine(item)) return false;
    return String(item.sku || '').toUpperCase().indexOf(EDP_SKU_PREFIX) === 0;
  }

  function getOrphanPackagingLines() {
    const items = cart.items || [];
    if (items.some(isEdpVialLine)) return [];
    return items.filter(isPackagingLine);
  }

  /* Removes any box left without EDPs. Returns true if it removed something. */
  async function removeOrphanPackaging() {
    const orphans = getOrphanPackagingLines();
    if (orphans.length === 0) return false;
    try {
      for (const line of orphans) {
        const res = await fetch(ROUTES.change, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: line.key, quantity: 0 }),
        });
        if (res.ok) cart = await res.json();
      }
      showToast(ORPHAN_MSG);
      return true;
    } catch (err) {
      console.warn('[SlideCart] Could not remove orphaned box:', err);
      await fetchCart();
      return false;
    }
  }

  /* Never let a packaging line go above the quantity the bundle put in. */
  function capPackagingQty(key, requested) {
    const line = getLineByKey(key);
    if (line && isPackagingLine(line) && requested > line.quantity) return line.quantity;
    return requested;
  }

  /* ── Mystery Limited Edition ─────────────────── */
  /* Resolved server-side in the Liquid snippet so price and
     availability are always correct for the customer's market. */
  const MYSTERY = CONFIG.mystery && CONFIG.mystery.enabled ? CONFIG.mystery : null;
  let mysteryQty = 0;              // optimistic local quantity
  let mysteryTimer = null;         // debounce timer for the stepper
  let mysteryQueue = Promise.resolve(); // serialises API writes so taps never drop
  let mysteryAttrSig = null;       // last synced attribute signature (avoids redundant writes)
  let mysteryHookAtAdd = null;     // which prompt was on screen when they first added

  /* ── Neutralize Impulse's native cart drawer ── */
  /* Impulse renders cart drawer HTML even in "Page" mode.
     Hide it and prevent its event listeners from interfering. */
  (function() {
    var selectors = [
      '.cart-drawer', '.ajaxcart', '.ajax-cart', '.drawer--cart',
      '[data-cart-drawer]', '[data-ajax-cart-drawer]', '#CartDrawer',
      '.drawer.drawer--right'
    ];
    selectors.forEach(function(sel) {
      document.querySelectorAll(sel).forEach(function(el) {
        el.style.display = 'none';
        el.setAttribute('aria-hidden', 'true');
      });
    });
    // Intercept Impulse's cart:open event
    document.addEventListener('cart:open', function(e) {
      e.stopImmediatePropagation();
    }, true);
  })();

  /* ── DOM refs ────────────────────────────────── */
  const drawer = document.getElementById('slideCart');
  if (!drawer) return;

  const els = {
    drawer,
    body: drawer.querySelector('[data-sc-body]'),
    items: drawer.querySelector('[data-sc-items]'),
    empty: drawer.querySelector('[data-sc-empty]'),
    count: drawer.querySelectorAll('[data-sc-count]'),
    subtotal: drawer.querySelector('[data-sc-subtotal]'),
    shipping: drawer.querySelector('[data-sc-shipping]'),
    shippingMsg: drawer.querySelector('[data-sc-shipping-msg]'),
    shippingBar: drawer.querySelector('[data-sc-shipping-bar]'),
    upsell: drawer.querySelector('[data-sc-upsell]'),
    upsellCarousel: drawer.querySelector('[data-sc-upsell-carousel]'),
    discountToggle: drawer.querySelector('[data-sc-discount-toggle]'),
    discountForm: drawer.querySelector('[data-sc-discount-form]'),
    discountInput: drawer.querySelector('[data-sc-discount-input]'),
    discountApply: drawer.querySelector('[data-sc-discount-apply]'),
    discountMsg: drawer.querySelector('[data-sc-discount-msg]'),
    savings: drawer.querySelector('[data-sc-savings]'),
    totalsNote: drawer.querySelector('[data-sc-totals-note]'),
    mystery: drawer.querySelector('[data-sc-mystery]'),
    mysteryText: drawer.querySelector('[data-sc-mystery-text]'),
    mysteryHook: drawer.querySelector('[data-sc-mystery-hook]'),
    mysteryAdd: drawer.querySelector('[data-sc-mystery-add]'),
    mysteryAddLabel: drawer.querySelector('[data-sc-mystery-add-label]'),
    mysteryStepper: drawer.querySelector('[data-sc-mystery-stepper]'),
    mysteryCount: drawer.querySelector('[data-sc-mystery-count]'),
    mysteryBadge: drawer.querySelector('[data-sc-mystery-badge]'),
    mysteryHint: drawer.querySelector('[data-sc-mystery-hint]'),
    mysteryMinus: drawer.querySelector('[data-sc-mystery-minus]'),
    mysteryPlus: drawer.querySelector('[data-sc-mystery-plus]'),
  };

  /* ═══════════════════════════════════════════════
     ADD-FLOW LOADING OVERLAY
     Injected here so custom-slide-cart.css needs no edit.
     ═══════════════════════════════════════════════ */

  const scLoadStyle = document.createElement('style');
  scLoadStyle.textContent = '.sc-body{position:relative}' +
    '.sc-body.sc-body--loading::after{content:"";position:absolute;inset:0;background:rgba(255,255,255,0.55);z-index:5}' +
    '.sc-body.sc-body--loading::before{content:"";position:absolute;top:38px;left:50%;width:26px;height:26px;margin-left:-13px;border:3px solid rgba(0,0,0,0.15);border-top-color:rgba(0,0,0,0.55);border-radius:50%;z-index:6;animation:scSpin 0.7s linear infinite}' +
    '@media(prefers-reduced-motion:reduce){.sc-body.sc-body--loading::before{animation:none}}' +
    '@keyframes scSpin{to{transform:rotate(360deg)}}' +
    '.sc-item__pack-qty{display:flex;flex-direction:column;gap:2px;line-height:1.3}' +
    '.sc-item__pack-qty-num{font-weight:600;font-size:14px}' +
    '.sc-item__pack-qty-note{font-size:12px;opacity:0.7}';
  document.head.appendChild(scLoadStyle);

  function setBodyLoading(on) {
    if (els.body) els.body.classList.toggle('sc-body--loading', !!on);
  }

  /* ═══════════════════════════════════════════════
     CART API
     ═══════════════════════════════════════════════ */

  async function fetchCart() {
    try {
      const res = await fetch(ROUTES.cart, { headers: { 'Accept': 'application/json' } });
      cart = await res.json();
      return cart;
    } catch (err) {
      console.error('[SlideCart] Failed to fetch cart:', err);
      return cart;
    }
  }

  async function addToCart(variantId, quantity = 1, properties = {}, sellingPlan = null) {
    if (isUpdating) return;
    isUpdating = true;

    const body = {
      id: variantId,
      quantity,
      properties,
    };
    if (sellingPlan) body.selling_plan = sellingPlan;

    // Open immediately with a loading state; the network work runs behind
    // the animation. Perceived speed: drawer moves on the same frame as
    // the click instead of after two round trips.
    openCart();
    setBodyLoading(true);

    try {
      const res = await fetch(ROUTES.add, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const err = await res.json();
        setBodyLoading(false);
        showToast(err.description || 'Could not add item');
        document.dispatchEvent(new CustomEvent('slideCart:addError'));
        return null;
      }

      const item = await res.json();
      await fetchCart();
      renderCart();
      setBodyLoading(false);
      document.dispatchEvent(new CustomEvent('slideCart:added', { detail: { item: item } }));
      return item;
    } catch (err) {
      console.error('[SlideCart] Add to cart error:', err);
      setBodyLoading(false);
      showToast('Something went wrong');
      document.dispatchEvent(new CustomEvent('slideCart:addError'));
      return null;
    } finally {
      isUpdating = false;
    }
  }

  async function changeQuantity(key, quantity) {
    if (isUpdating) return;

    // Last line of defence: packaging lines can go down, never up
    const capped = capPackagingQty(key, quantity);
    if (capped !== quantity) {
      showToast(PACKAGING_MSG);
      renderLineItems();
      return;
    }

    isUpdating = true;

    // Optimistic UI — update visually first
    const itemEl = els.items.querySelector(`[data-key="${key}"]`);
    if (itemEl) itemEl.setAttribute('data-loading', 'true');

    try {
      await fetch(ROUTES.change, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ id: key, quantity }),
      });

      // Always re-fetch full cart to get complete discount data
      await fetchCart();
      await removeOrphanPackaging();
      renderCart();
    } catch (err) {
      console.error('[SlideCart] Change quantity error:', err);
      showToast('Could not update quantity');
      await fetchCart();
      renderCart();
    } finally {
      isUpdating = false;
    }
  }

  async function removeItem(key) {
    if (isUpdating) return;
    isUpdating = true;

    const itemEl = els.items.querySelector(`[data-key="${key}"]`);
    
    // Run animation and API call in parallel
    const animPromise = new Promise(r => {
      if (itemEl) {
        itemEl.classList.add('sc-item--removing');
        itemEl.addEventListener('animationend', r, { once: true });
        // Fallback in case animationend doesn't fire
        setTimeout(r, 300);
      } else {
        r();
      }
    });

    const apiPromise = fetch(ROUTES.change, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({ id: key, quantity: 0 }),
    });

    try {
      await Promise.all([animPromise, apiPromise]);
      // Re-fetch full cart for complete discount data
      await fetchCart();
      await removeOrphanPackaging();
      renderCart();
    } catch (err) {
      console.error('[SlideCart] Remove error:', err);
      await fetchCart();
      renderCart();
    } finally {
      isUpdating = false;
    }
  }

  /* ═══════════════════════════════════════════════
     RENDER
     ═══════════════════════════════════════════════ */

  function renderCart() {
    renderItemCount();
    renderShipping();
    renderSubtotal();
    renderSavings();
    renderTotalsNote();
    renderLineItems();
    renderEmptyState();
    renderBundlePromo();
    renderMystery();
    fetchAndRenderUpsells();
    updateBubbleCount();

    // Any item we don't have a compare-at price for yet gets looked up.
    // If the lookup finds one, repaint the affected parts only.
    hydrateCompareAt().then(learned => {
      if (learned) {
        renderLineItems();
        renderSavings();
      }
    });
  }

  function renderItemCount() {
    els.count.forEach(el => {
      el.textContent = cart.item_count;
    });
  }

  function renderSubtotal() {
    if (els.subtotal) {
      els.subtotal.textContent = formatMoney(cart.total_price);
    }
    // Discount rows used to be rebuilt here and placed below the subtotal.
    // They now render as named components inside renderSavings() instead —
    // cart.total_price is already net, so a deduction row underneath read as
    // a second discount still to be applied.
  }

  function renderEmptyState() {
    const hasItems = cart.item_count > 0;
    els.empty.classList.toggle('sc-hidden', hasItems);
    els.items.classList.toggle('sc-hidden', !hasItems);
  }

  /* ── Bundle builder promo ───────────────────── */
  function renderBundlePromo() {
    const promo = drawer.querySelector('[data-sc-bundle-promo]');
    if (!promo) return;

    // Only hide if cart already has a subscription item
    const hasSubscription = cart.items.some(i => !!i.selling_plan_allocation);
    promo.classList.toggle('sc-hidden', hasSubscription);
  }

  /* ── Free shipping threshold ───────────────────
     Returns the threshold in the customer's own currency, in cents, or 0
     if it can't be worked out safely.

     When the customer pays in the threshold's currency, it's used as is.
     When they don't (e.g. a Swedish shopper paying in kr against the €90
     EU threshold), it's converted with the live rate Shopify puts on every
     storefront page (Shopify.currency.rate, relative to the shop's base
     currency). It's rounded UP to a whole unit so the drawer never says
     free shipping before checkout agrees. */
  function getShippingThresholdCents() {
    if (!els.shipping) return 0;
    const threshold = parseFloat(els.shipping.dataset.threshold);
    if (!threshold || threshold <= 0) return 0;

    if (els.shipping.dataset.currencyMatch === 'true') {
      return Math.round(threshold * 100);
    }

    const thresholdCode = els.shipping.dataset.currencyCode;
    const baseCode = CONFIG.shopCurrency || 'EUR';
    const rate = window.Shopify && window.Shopify.currency
      ? parseFloat(window.Shopify.currency.rate)
      : NaN;

    if (thresholdCode !== baseCode || !rate || rate <= 0) return 0;
    return Math.ceil(threshold * rate) * 100;
  }

  /* The Liquid only draws the progress bar when the currencies match.
     When we've converted the threshold, add the bar here and pick up the
     message element so it can be rewritten in the customer's currency. */
  function ensureShippingElements() {
    if (!els.shipping) return;
    if (!els.shippingMsg) {
      els.shippingMsg = els.shipping.querySelector('.sc-shipping__message');
    }
    if (!els.shippingBar) {
      const track = document.createElement('div');
      track.className = 'sc-shipping__track';
      track.innerHTML = '<div class="sc-shipping__bar" data-sc-shipping-bar style="width: 0%"><span class="sc-shipping__pip"></span></div>';
      els.shipping.appendChild(track);
      els.shippingBar = track.querySelector('[data-sc-shipping-bar]');
    }
  }

  /* ── Shipping progress bar ─────────────────── */
  function renderShipping() {
    if (!els.shipping) return;

    const thresholdCents = getShippingThresholdCents();

    // No safe threshold for this currency: leave the server-rendered text alone
    if (!thresholdCents) return;

    if (els.shipping.dataset.currencyMatch !== 'true') ensureShippingElements();

    const cartTotal = cart.total_price || 0;
    const remainingCents = Math.max(0, thresholdCents - cartTotal);
    const pct = Math.min(100, (cartTotal / thresholdCents) * 100);
    const isComplete = remainingCents <= 0;

    // Update progress bar
    if (els.shippingBar) {
      els.shippingBar.style.width = `${pct}%`;
      els.shippingBar.setAttribute('data-complete', isComplete);
    }

    // Update message
    if (els.shippingMsg) {
      if (isComplete) {
        els.shippingMsg.innerHTML = `You've unlocked <strong>free shipping!</strong> 🎉`;
      } else {
        els.shippingMsg.innerHTML = `Spend <strong>${formatMoney(remainingCents)}</strong> more for free shipping`;
      }
    }
  }

  /* The note under the subtotal has to track the free shipping threshold,
     because crossing it client-side makes "shipping calculated at checkout"
     contradict the banner at the top of the drawer. Tax wording comes from
     cart.taxes_included, which is market-aware. */
  function renderTotalsNote() {
    if (!els.totalsNote) return;

    const taxesIncluded = CONFIG.taxesIncluded === true;

    const thresholdCents = getShippingThresholdCents();
    const freeShipUnlocked = thresholdCents > 0 && (cart.total_price || 0) >= thresholdCents;

    let text;
    if (freeShipUnlocked && taxesIncluded) {
      text = 'Free shipping applied \u00b7 taxes included';
    } else if (freeShipUnlocked) {
      text = 'Free shipping applied \u00b7 taxes calculated at checkout';
    } else if (taxesIncluded) {
      text = 'Taxes included \u00b7 shipping calculated at checkout';
    } else {
      text = 'Shipping & taxes calculated at checkout';
    }

    els.totalsNote.textContent = text;
    els.totalsNote.classList.toggle('sc-totals__note--positive', freeShipUnlocked);
  }

  /* ═══════════════════════════════════════════════
     COMPARE-AT & SAVINGS
     ═══════════════════════════════════════════════ */

  /* Unit compare-at for a line, or 0 if there isn't one. */
  function getCompareAt(item) {
    const cached = compareAtCache[item.variant_id];
    return typeof cached === 'number' ? cached : 0;
  }

  /* Pre-discount unit price. cart.js exposes original_price on newer themes;
     fall back to price where it doesn't. */
  function getUnitPrice(item) {
    return typeof item.original_price === 'number' ? item.original_price : item.price;
  }

  /* Fetch compare_at_price for any cart item we don't have cached yet.
     One request per product handle, cached, never repeated. */
  async function hydrateCompareAt() {
    if (!cart.items || cart.items.length === 0) return false;

    const needed = cart.items.filter(item =>
      compareAtCache[item.variant_id] === undefined &&
      item.handle &&
      !compareAtPending.has(item.handle) &&
      !compareAtMissing.has(item.handle)
    );
    if (needed.length === 0) return false;

    const handles = [...new Set(needed.map(i => i.handle))];
    handles.forEach(h => compareAtPending.add(h));

    let learnedSomething = false;

    await Promise.all(handles.map(async (handle) => {
      try {
        const res = await fetch(`${CONFIG.rootUrl || '/'}products/${handle}.js`, {
          headers: { 'Accept': 'application/json' },
        });
        if (!res.ok) throw new Error(`${res.status}`);
        const product = await res.json();

        let found = false;
        (product.variants || []).forEach(v => {
          if (v.compare_at_price && v.compare_at_price > v.price) {
            compareAtCache[v.id] = v.compare_at_price;
            found = true;
            learnedSomething = true;
          } else {
            // Cache the absence too, so we don't re-check on every render
            compareAtCache[v.id] = 0;
          }
        });
        if (!found) compareAtMissing.add(handle);
      } catch (err) {
        // A failed lookup just means no strikethrough — never block the cart
        console.warn('[SlideCart] compare-at lookup failed for', handle, err);
        compareAtMissing.add(handle);
      } finally {
        compareAtPending.delete(handle);
      }
    }));

    return learnedSomething;
  }

  /* Compare-at portion only — the gap between compare_at_price and what
     they're actually paying. Excludes Shopify discount applications. */
  function calculateCompareSavings() {
    if (!cart.items || cart.items.length === 0) return 0;

    let compareSavings = 0;
    cart.items.forEach(item => {
      const ca = getCompareAt(item);
      const unit = getUnitPrice(item);
      if (ca > unit) compareSavings += (ca - unit) * item.quantity;
    });
    return compareSavings;
  }

  /* Total savings = compare-at gap + Shopify discount applications.
     The two never overlap: cart.total_discount reflects discount applications
     only and is blind to compare_at_price. */
  function calculateSavings() {
    return calculateCompareSavings() + (cart.total_discount || 0);
  }

  /* Line-level discounts (automatic bundle discounts, BOGOs, Loop subscription
     discounts) don't appear in cart_level_discount_applications. They live on
     each line item, and each allocation carries the real customer-facing name.
     Group them by title so one bundle discount spread across eight lines shows
     as a single named row rather than eight fragments. */
  function getLineLevelDiscounts() {
    const byTitle = new Map();
    (cart.items || []).forEach(item => {
      (item.line_level_discount_allocations || []).forEach(alloc => {
        const title = alloc && alloc.discount_application && alloc.discount_application.title;
        const amount = alloc && alloc.amount;
        if (!title || typeof amount !== 'number' || amount <= 0) return;
        byTitle.set(title, (byTitle.get(title) || 0) + amount);
      });
    });
    return [...byTitle.entries()].map(([title, amount]) => ({ title, amount }));
  }

  /* Break the savings into named components when there's more than one source,
     so "You're saving €45.35" isn't an opaque blend of bundle value and codes. */
  function renderSavings() {
    if (!els.savings) return;

    const compareSavings = calculateCompareSavings();
    const totalDiscount = cart.total_discount || 0;

    const cartLevel = (cart.cart_level_discount_applications || [])
      .filter(d => d && d.title && typeof d.total_allocated_amount === 'number' && d.total_allocated_amount > 0)
      .map(d => ({ title: d.title, amount: d.total_allocated_amount }));

    const lineLevel = getLineLevelDiscounts();
    const named = [...cartLevel, ...lineLevel];
    const namedTotal = named.reduce((sum, d) => sum + d.amount, 0);

    const totalSavings = compareSavings + totalDiscount;
    if (totalSavings <= 0) {
      els.savings.innerHTML = '';
      return;
    }

    /* Safety valve: if the named parts exceed total_discount, something has
       been counted twice (a discount surfacing in both lists). Rather than
       show a breakdown that doesn't add up, fall back to the total alone. */
    const partsAreSane = namedTotal <= totalDiscount;
    const remainder = partsAreSane ? totalDiscount - namedTotal : 0;

    let sourceCount = 0;
    if (compareSavings > 0) sourceCount++;
    sourceCount += named.length;
    if (remainder > 0) sourceCount++;

    let html = '';

    if (partsAreSane && sourceCount > 1) {
      if (compareSavings > 0) html += part('Bundle savings', compareSavings);
      named.forEach(d => { html += part(d.title, d.amount); });
      if (remainder > 0) html += part('Discount', remainder);
    }

    const label = CONFIG.savingsLabel || "You're saving";
    html += `
      <div class="sc-totals__row sc-totals__row--savings">
        <span>${escapeHtml(label)}</span>
        <span class="sc-totals__savings-amount">${formatMoney(totalSavings)}</span>
      </div>`;

    els.savings.innerHTML = html;

    function part(name, amount) {
      return `
      <div class="sc-totals__row sc-totals__row--savings-part">
        <span>${escapeHtml(name)}</span>
        <span>&minus;${formatMoney(amount)}</span>
      </div>`;
    }
  }

  /* ── Line items ────────────────────────────── */
  function renderLineItems() {
    if (!cart.items || cart.items.length === 0) {
      els.items.innerHTML = '';
      return;
    }

    els.items.innerHTML = cart.items.map((item, index) => {
      // Two sources of "was" pricing: a compare-at price (bundles priced below
      // component value) or a real discount application. Higher one wins.
      const compareAtLine = getCompareAt(item) * item.quantity;
      const wasLine = Math.max(compareAtLine, item.original_line_price);
      const hasCompare = wasLine > item.final_line_price;
      const hasVariant = item.variant_title && item.variant_title !== 'Default Title';
      const hasSub = item.selling_plan_allocation;
      const locked = isPackagingLine(item);

      return `
        <div class="sc-item" data-sc-item data-line="${index + 1}" data-variant-id="${item.variant_id}" data-key="${item.key}">
          <a href="${item.url}" class="sc-item__img-wrap">
            ${item.featured_image && item.featured_image.url
              ? `<img src="${getSizedImageUrl(item.featured_image.url, '200x')}" alt="${escapeHtml(item.title)}" class="sc-item__img" width="100" height="100" loading="lazy">`
              : `<div class="sc-item__img sc-item__img--placeholder">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" stroke-width="1.5"/>
                    <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor"/>
                    <path d="M21 15l-5-5L5 21" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>`
            }
          </a>

          <div class="sc-item__details">
            <a href="${item.url}" class="sc-item__title">${escapeHtml(item.product_title)}</a>
            ${hasVariant ? `<p class="sc-item__variant">${escapeHtml(item.variant_title)}</p>` : ''}
            ${hasSub ? `
              <p class="sc-item__subscription">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M11.5 7.5A4.5 4.5 0 117 2.5M7 2.5V5l2-1.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                ${escapeHtml(item.selling_plan_allocation.selling_plan.name)}
              </p>` : ''}
            <div class="sc-item__bottom">
              ${locked ? packagingQtyHtml(item.quantity) : `<div class="sc-item__qty">
                <button class="sc-item__qty-btn" data-sc-qty-minus aria-label="Decrease quantity">
                  <svg width="12" height="12" viewBox="0 0 12 12"><path d="M2.5 6h7" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>
                </button>
                <input type="number" class="sc-item__qty-input" value="${item.quantity}" min="0" max="99" data-sc-qty-input aria-label="Quantity">
                <button class="sc-item__qty-btn" data-sc-qty-plus aria-label="Increase quantity">
                  <svg width="12" height="12" viewBox="0 0 12 12"><path d="M6 2.5v7M2.5 6h7" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>
                </button>
              </div>`}
              <div class="sc-item__prices">
                ${hasCompare ? `<span class="sc-item__price sc-item__price--compare">${formatMoney(wasLine)}</span>` : ''}
                <span class="sc-item__price">${formatMoney(item.final_line_price)}</span>
              </div>
            </div>
          </div>

          <button class="sc-item__remove" data-sc-remove aria-label="Remove ${escapeHtml(item.title)}">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
      `;
    }).join('');

    // Re-bind item events
    bindItemEvents();
  }

  /* ═══════════════════════════════════════════════
     MYSTERY LIMITED EDITION
     ═══════════════════════════════════════════════ */

  /* All lines in the cart matching the mystery variant. */
  function getMysteryLines() {
    if (!MYSTERY || !cart.items) return [];
    return cart.items.filter(i => i.variant_id === MYSTERY.variantId);
  }

  function getMysteryQtyInCart() {
    return getMysteryLines().reduce((sum, i) => sum + i.quantity, 0);
  }

  /* How many dips would close the free shipping gap.
     Returns 0 if there's no gap, or if we can't work out the threshold safely. */
  function mysteryDipsToFreeShipping() {
    if (!MYSTERY) return 0;

    const thresholdCents = getShippingThresholdCents();
    if (!thresholdCents) return 0;

    const remainingCents = thresholdCents - (cart.total_price || 0);
    if (remainingCents <= 0) return 0;
    if (!MYSTERY.price || MYSTERY.price <= 0) return 0;

    return Math.ceil(remainingCents / MYSTERY.price);
  }

  function renderMystery() {
    if (!MYSTERY || !els.mystery) return;

    // Only an add-on — never shown against an empty cart
    const hasItems = cart.item_count > 0;
    els.mystery.classList.toggle('sc-hidden', !hasItems);
    if (!hasItems) {
      syncMysteryAttributes(0);
      return;
    }

    const qty = getMysteryQtyInCart();
    mysteryQty = qty;
    const max = MYSTERY.max || 5;
    const atMax = qty >= max;

    els.mystery.dataset.qty = qty;
    els.mystery.classList.toggle('sc-mystery--active', qty > 0);

    // Add button ↔ stepper
    if (els.mysteryAdd) els.mysteryAdd.hidden = qty > 0;
    if (els.mysteryStepper) els.mysteryStepper.hidden = qty === 0;
    if (els.mysteryCount) els.mysteryCount.textContent = qty;

    // Corner badge on the image
    if (els.mysteryBadge) {
      els.mysteryBadge.hidden = qty === 0;
      els.mysteryBadge.textContent = qty;
    }

    // Cap the plus button
    if (els.mysteryPlus) {
      els.mysteryPlus.disabled = atMax;
      els.mysteryPlus.setAttribute('aria-disabled', String(atMax));
    }

    // Body copy escalates with quantity
    if (els.mysteryText) {
      const c = MYSTERY.copy || {};
      let text = c.base || '';
      if (atMax && qty > 0) text = c.maxed || c.many || text;
      else if (qty >= 3) text = c.many || text;
      else if (qty === 2) text = c.two || text;
      else if (qty === 1) text = c.one || text;
      els.mysteryText.textContent = text;
    }

    // Stepper hint
    if (els.mysteryHint) {
      els.mysteryHint.textContent = atMax ? 'Max reached' : 'Add another';
      els.mysteryHint.classList.toggle('sc-mystery__stepper-hint--muted', atMax);
    }

    renderMysteryHook(qty, atMax);

    /* Covers paths that don't go through the stepper — e.g. removing the item
       with the line-item X, or an external cart:updated event. Cheap: it
       short-circuits when the attribute signature hasn't changed. */
    syncMysteryAttributes(qty);
  }

  /* The honest bit: real arithmetic against the free shipping threshold.
     No invented offers — just "N of these closes your gap". */
  function renderMysteryHook(qty, atMax) {
    if (!els.mysteryHook) return;

    const dips = mysteryDipsToFreeShipping();
    const max = MYSTERY.max || 5;

    if (dips > 0 && dips <= (max - qty) && !atMax) {
      const label = dips === 1 ? 'one Lucky Dip' : `${dips} Lucky Dips`;
      els.mysteryHook.innerHTML =
        `Adding <strong>${label}</strong> tips you over the free shipping threshold.`;
      els.mysteryHook.hidden = false;
      els.mysteryHook.dataset.tone = 'gap';
      return;
    }

    if (dips === 0 && qty > 0) {
      els.mysteryHook.textContent = 'Free shipping unlocked — the surprise rides along.';
      els.mysteryHook.hidden = false;
      els.mysteryHook.dataset.tone = 'done';
      return;
    }

    els.mysteryHook.hidden = true;
  }

  /* Optimistic stepper — repaint immediately, commit after a beat. */
  function setMysteryQuantity(next) {
    if (!MYSTERY) return;

    const max = MYSTERY.max || 5;
    next = Math.max(0, Math.min(max, next));
    mysteryQty = next;

    // Immediate visual feedback
    if (els.mysteryCount) els.mysteryCount.textContent = next;
    if (els.mysteryBadge) {
      els.mysteryBadge.hidden = next === 0;
      els.mysteryBadge.textContent = next;
    }
    if (els.mysteryAdd) els.mysteryAdd.hidden = next > 0;
    if (els.mysteryStepper) els.mysteryStepper.hidden = next === 0;
    if (els.mystery) {
      els.mystery.dataset.qty = next;
      els.mystery.classList.toggle('sc-mystery--active', next > 0);
      els.mystery.setAttribute('data-loading', 'true');
    }
    if (els.mysteryPlus) els.mysteryPlus.disabled = next >= max;

    clearTimeout(mysteryTimer);
    mysteryTimer = setTimeout(() => {
      mysteryQueue = mysteryQueue.then(() => commitMysteryQuantity(next));
    }, 400);
  }

  async function commitMysteryQuantity(target) {
    if (!MYSTERY) return;

    const lines = getMysteryLines();

    try {
      if (lines.length === 0 && target > 0) {
        // Not in the cart yet — plain add
        const res = await fetch(ROUTES.add, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: MYSTERY.variantId, quantity: target }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          // Seasonal product — may have sold out mid-session
          showToast(err.description || 'That one just sold out');
          if (/sold out|unavailable|not enough/i.test(err.description || '')) {
            hideMysteryModule();
          }
        }
      } else if (lines.length > 0) {
        // Already in the cart — set the first line, clear any duplicates
        await fetch(ROUTES.change, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: lines[0].key, quantity: target }),
        });
        for (let i = 1; i < lines.length; i++) {
          await fetch(ROUTES.change, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id: lines[i].key, quantity: 0 }),
          });
        }
      }
    } catch (err) {
      console.error('[SlideCart] Mystery update error:', err);
      showToast('Could not update — try again');
    } finally {
      await fetchCart();
      await syncMysteryAttributes(getMysteryQtyInCart());
      if (els.mystery) els.mystery.removeAttribute('data-loading');
      renderCart();
    }
  }

  function hideMysteryModule() {
    if (els.mystery) els.mystery.classList.add('sc-hidden');
  }

  /* ── Order attribution ────────────────────────
     The storefront cannot write order tags — no browser-side API has that
     permission. What it CAN do is set cart attributes, which carry through
     checkout and land on the order as custom attributes. Shopify Flow then
     reads those and applies the actual tag.

     Attributes are kept in lockstep with the real cart quantity, so removing
     the item clears the attribution rather than leaving a false positive. */
  async function syncMysteryAttributes(qty) {
    if (!MYSTERY) return;

    const key = MYSTERY.attrKey || 'mystery_upsell';
    const attrs = {};

    if (qty > 0) {
      attrs[key] = 'slide-cart';
      attrs[key + '_qty'] = String(qty);
      attrs[key + '_hook'] = mysteryHookAtAdd || 'none';
    } else {
      // Empty string removes the attribute; other cart attributes are untouched
      attrs[key] = '';
      attrs[key + '_qty'] = '';
      attrs[key + '_hook'] = '';
      mysteryHookAtAdd = null;
    }

    const sig = JSON.stringify(attrs);
    if (sig === mysteryAttrSig) return; // nothing changed — don't burn a request

    try {
      const res = await fetch(ROUTES.update, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ attributes: attrs }),
      });
      if (res.ok) {
        mysteryAttrSig = sig;
        cart = await res.json(); // update.js returns the full cart
      }
    } catch (err) {
      // Attribution is not worth breaking the cart over — fail quietly
      console.warn('[SlideCart] Could not sync mystery attributes:', err);
    }
  }

  /* Note the on-screen prompt at the moment of the first add, so you can tell
     whether the free-shipping maths is what's actually driving conversions. */
  function captureMysteryHook() {
    if (mysteryHookAtAdd) return; // first add wins
    if (els.mysteryHook && !els.mysteryHook.hidden) {
      mysteryHookAtAdd = els.mysteryHook.dataset.tone === 'gap' ? 'shipping-gap' : 'shipping-met';
    } else {
      mysteryHookAtAdd = 'none';
    }
  }

  function bindMysteryEvents() {
    if (!MYSTERY || !els.mystery) return;

    if (els.mysteryAdd) {
      els.mysteryAdd.addEventListener('click', (e) => {
        e.preventDefault();
        captureMysteryHook();
        els.mystery.classList.add('sc-mystery--pop');
        setTimeout(() => els.mystery.classList.remove('sc-mystery--pop'), 400);
        setMysteryQuantity(getMysteryQtyInCart() + 1);
      });
    }

    if (els.mysteryPlus) {
      els.mysteryPlus.addEventListener('click', (e) => {
        e.preventDefault();
        captureMysteryHook();
        setMysteryQuantity(mysteryQty + 1);
      });
    }

    if (els.mysteryMinus) {
      els.mysteryMinus.addEventListener('click', (e) => {
        e.preventDefault();
        setMysteryQuantity(mysteryQty - 1);
      });
    }
  }

  /* ── Upsells (Product Recommendations API) ── */
  async function fetchAndRenderUpsells() {
    if (!els.upsellCarousel) return;
    if (cart.items.length === 0) {
      els.upsell.setAttribute('data-empty', 'true');
      return;
    }

    // Use the first product in the cart as the recommendation seed.
    // Skip the mystery box — it's a poor seed and skews recommendations.
    // Skip packaging boxes too — they'd only pull in other boxes.
    const seedItem = cart.items.find(i => (!MYSTERY || i.product_id !== MYSTERY.productId) && !isPackagingLine(i))
      || cart.items[0];
    const productId = seedItem.product_id;
    const cartVariantIds = cart.items.map(i => i.variant_id);

    let products = [];

    try {
      // Try complementary recommendations first
      const res = await fetch(
        `${CONFIG.rootUrl || '/'}recommendations/products.json?product_id=${productId}&limit=8&intent=complementary`
      );
      if (res.ok) {
        const data = await res.json();
        products = data.products || [];
      }

      // If too few, also try related
      if (products.length < 3) {
        const res2 = await fetch(
          `${CONFIG.rootUrl || '/'}recommendations/products.json?product_id=${productId}&limit=8&intent=related`
        );
        if (res2.ok) {
          const data2 = await res2.json();
          const combined = [...products, ...data2.products];
          const seen = new Set();
          products = combined.filter(p => {
            if (seen.has(p.id)) return false;
            seen.add(p.id);
            return true;
          });
        }
      }
    } catch (err) {
      console.warn('[SlideCart] Recommendations unavailable:', err);
    }

    // Filter out ineligible products
    const filtered = filterUpsellProducts(products, cartVariantIds);

    // If still empty, fallback to beard & shaving collections
    if (filtered.length === 0) {
      try {
        const fallbackProducts = [];
        const collections = ['beard', 'shaving'];
        for (const handle of collections) {
          const res = await fetch(`${CONFIG.rootUrl || '/'}collections/${handle}/products.json?limit=6`);
          if (res.ok) {
            const data = await res.json();
            fallbackProducts.push(...(data.products || []));
          }
        }
        // De-duplicate
        const seen = new Set();
        const uniqueFallback = fallbackProducts.filter(p => {
          if (seen.has(p.id)) return false;
          seen.add(p.id);
          return true;
        });
        const fallbackFiltered = filterUpsellProducts(uniqueFallback, cartVariantIds);
        renderUpsellCards(fallbackFiltered, cartVariantIds, true);
      } catch (err) {
        console.warn('[SlideCart] Fallback collections unavailable:', err);
        els.upsell.setAttribute('data-empty', 'true');
      }
    } else {
      renderUpsellCards(filtered, cartVariantIds, false);
    }
  }

  function filterUpsellProducts(products, cartVariantIds) {
    return products.filter(p => {
      if (!p.available) return false;
      // Packaging boxes only ship inside a bundle — never offer them on their own
      if (isPackagingProduct(p)) return false;
      // The mystery box has its own dedicated module — don't show it twice
      if (MYSTERY && (p.id === MYSTERY.productId || p.handle === MYSTERY.handle)) return false;
      // Skip products tagged as sold_out or mainline-edp
      if (p.tags && (Array.isArray(p.tags) ? p.tags : p.tags.split(',')).map(t => t.trim().toLowerCase()).some(t => t === 'sold_out' || t === 'mainline-edp')) return false;
      // Find first available variant not already in cart
      const availableVariant = p.variants.find(v => v.available && !cartVariantIds.includes(v.id) && !PACKAGING_VARIANT_IDS.has(Number(v.id)));
      return !!availableVariant;
    }).slice(0, 6);
  }

  function renderUpsellCards(filtered, cartVariantIds, isFallback) {
    if (filtered.length === 0) {
      els.upsell.setAttribute('data-empty', 'true');
      els.upsellCarousel.innerHTML = '';
      return;
    }

    // Update heading if showing fallback collection products
    const heading = els.upsell.querySelector('.sc-upsell__title');
    if (heading) {
      heading.textContent = isFallback ? 'You Might Also Like' : (CONFIG.upsellTitle || 'Complete Your Routine');
    }

    els.upsell.removeAttribute('data-empty');
    els.upsellCarousel.innerHTML = filtered.map(product => {
      const variant = product.variants.find(v => v.available && !cartVariantIds.includes(v.id));
      if (!variant) return '';
      const hasCompare = variant.compare_at_price && variant.compare_at_price > variant.price;
      const imgUrl = product.featured_image
        ? getSizedImageUrl(product.featured_image, '300x')
        : '';

      return `
        <div class="sc-upsell-card" data-product-id="${product.id}">
          <a href="${product.url}" class="sc-upsell-card__img-wrap">
            ${imgUrl
              ? `<img src="${imgUrl}" alt="${escapeHtml(product.title)}" class="sc-upsell-card__img" loading="lazy" width="150" height="150">`
              : ''
            }
          </a>
          <div class="sc-upsell-card__info">
            <a href="${product.url}" class="sc-upsell-card__name">${escapeHtml(product.title)}</a>
            <div class="sc-upsell-card__bottom">
              <div>
                ${hasCompare ? `<span class="sc-upsell-card__price sc-upsell-card__price--compare">${formatMoney(variant.compare_at_price)}</span> ` : ''}
                <span class="sc-upsell-card__price">${formatMoney(variant.price)}</span>
              </div>
              <button class="sc-upsell-card__add" data-variant-id="${variant.id}" aria-label="Add ${escapeHtml(product.title)} to cart">
                <svg viewBox="0 0 14 14" fill="none"><path d="M7 2.5v9M2.5 7h9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
              </button>
            </div>
          </div>
        </div>
      `;
    }).join('');

    // Bind upsell add buttons
    els.upsellCarousel.querySelectorAll('.sc-upsell-card__add').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        const vid = parseInt(btn.dataset.variantId);
        btn.classList.add('sc-upsell-card__add--added');
        btn.innerHTML = `<svg viewBox="0 0 14 14" fill="none"><path d="M3 7l3 3 5-5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
        await addToCart(vid, 1);
      });
    });
  }

  /* ═══════════════════════════════════════════════
     OPEN / CLOSE
     ═══════════════════════════════════════════════ */

  function openCart() {
    isOpen = true;
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('sc-open');

    // Trap focus
    requestAnimationFrame(() => {
      const firstFocusable = drawer.querySelector('button, [href], input, select, textarea');
      if (firstFocusable) firstFocusable.focus();
    });

    // Dispatch event for integrations
    document.dispatchEvent(new CustomEvent('slideCart:open'));
  }

  function closeCart() {
    isOpen = false;
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('sc-open');
    document.dispatchEvent(new CustomEvent('slideCart:close'));
  }

  function toggleCart() {
    isOpen ? closeCart() : openCart();
  }

  /* ═══════════════════════════════════════════════
     EVENT BINDING
     ═══════════════════════════════════════════════ */

  function bindGlobalEvents() {
    // Close buttons & overlay
    drawer.querySelectorAll('[data-sc-close]').forEach(el => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        closeCart();
      });
    });

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && isOpen) closeCart();
    });

    // Cart icon / trigger buttons — use capture phase to intercept before Impulse's own cart drawer JS
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest([
        '[data-sc-toggle]',
        '.cart-link',
        'a[href="/cart"]',
        'a[href$="/cart"]',
        '.js-drawer-open-cart',
        '[data-cart-open]',
        '.site-header__cart',
        '.mobile-nav__cart',
        '.header__cart-btn',
        '.cart-icon',
        '[aria-controls="cart-drawer"]',
        'button[data-cart-toggle]',
        '.site-header__icon--cart',
        '.header-cart',
      ].join(','));
      
      if (!trigger) return;
      // Don't hijack if modifier keys held (open in new tab)
      if (e.metaKey || e.ctrlKey) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      toggleCart();
    }, true);

    /* ═══════════════════════════════════════════════
       CC3 BUNDLE CODE GUARD
       A Clean Craic bundle's discount code only lives in the
       session cookie set by the builder's own checkout button.
       Any other route into checkout (this drawer's button, the
       cart page button) loses it, and the customer sees full
       price on items the builder promised free. The builder now
       stamps the intended code on the cart as the
       cc3_discount_code attribute; when it's present, reroute
       checkout through the discount URL so the code re-applies.
       ═══════════════════════════════════════════════ */
    const CHECKOUT_SELECTOR = [
      'a[href="/checkout"]',
      'a[href$="/checkout"]',
      'a[href="/cart/checkout"]',
      'a[href$="/cart/checkout"]',
      'button[name="checkout"]',
      'input[name="checkout"]',
      '[data-sc-checkout]',
    ].join(',');

    /* ORPHANED BOX GUARD
       Registered before the CC3 guard so it runs first. If the cart holds a
       box, re-read the cart fresh (the /cart page changes quantities without
       telling this script), strip any box left without EDPs, then carry on to
       checkout by the same route the CC3 guard would have used. */
    document.addEventListener('click', async (e) => {
      const target = e.target.closest(CHECKOUT_SELECTOR);
      if (!target) return;
      if (e.metaKey || e.ctrlKey) return;
      if (!(cart.items || []).some(isPackagingLine)) return;
      e.preventDefault();
      e.stopImmediatePropagation();

      await fetchCart();
      const removed = await removeOrphanPackaging();

      if (removed && (!cart.items || cart.items.length === 0)) {
        // Nothing left to buy: stay put and show the empty cart
        if (/\/cart\/?$/.test(window.location.pathname)) {
          window.location.reload();
        } else {
          renderCart();
        }
        return;
      }
      goToCheckout();
    }, true);

    document.addEventListener('click', (e) => {
      const target = e.target.closest(CHECKOUT_SELECTOR);
      if (!target) return;
      if (e.metaKey || e.ctrlKey) return;
      const code = cart && cart.attributes && cart.attributes.cc3_discount_code;
      if (!code || code === 'none') return;
      e.preventDefault();
      e.stopImmediatePropagation();
      goToCheckout();
    }, true);

    // Intercept ALL add-to-cart forms (capture phase to fire before Impulse)
    document.addEventListener('submit', async (e) => {
      const form = e.target;
      if (!form.matches('form[action*="/cart/add"]')) return;
      e.preventDefault();
      e.stopImmediatePropagation();

      const formData = new FormData(form);
      const variantId = parseInt(formData.get('id'));
      const quantity = parseInt(formData.get('quantity')) || 1;
      const sellingPlan = formData.get('selling_plan') || null;

      // Gather properties
      const properties = {};
      for (const [key, value] of formData.entries()) {
        if (key.startsWith('properties[')) {
          const propKey = key.replace('properties[', '').replace(']', '');
          properties[propKey] = value;
        }
      }

      // Disable submit button temporarily
      const submitBtn = form.querySelector('[type="submit"], button:not([type])');
      if (submitBtn) {
        submitBtn.disabled = true;
        setTimeout(() => { submitBtn.disabled = false; }, 1500);
      }

      await addToCart(variantId, quantity, properties, sellingPlan);
    }, true);

    // Discount toggle
    if (els.discountToggle) {
      els.discountToggle.setAttribute('aria-expanded', 'false');
      els.discountForm.setAttribute('aria-hidden', 'true');

      els.discountToggle.addEventListener('click', () => {
        const expanded = els.discountToggle.getAttribute('aria-expanded') === 'true';
        els.discountToggle.setAttribute('aria-expanded', !expanded);
        els.discountForm.setAttribute('aria-hidden', expanded);
      });
    }

    // Popular bundle add-to-cart buttons (empty cart state)
    drawer.querySelectorAll('[data-sc-popular-add]').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        const vid = parseInt(btn.dataset.variantId);
        const originalText = btn.textContent;
        btn.textContent = 'Adding...';
        btn.disabled = true;
        await addToCart(vid, 1);
        btn.textContent = 'Added ✓';
        btn.classList.add('sc-popular__add--added');
        setTimeout(() => {
          btn.textContent = originalText;
          btn.classList.remove('sc-popular__add--added');
          btn.disabled = false;
        }, 2000);
      });
    });

    // Discount apply
    if (els.discountApply) {
      els.discountApply.addEventListener('click', applyDiscount);
      els.discountInput?.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') applyDiscount();
      });
    }

  }

  /* Checkout, carrying the CC3 bundle code through when the cart has one. */
  function goToCheckout() {
    const code = cart && cart.attributes && cart.attributes.cc3_discount_code;
    if (code && code !== 'none') {
      window.location.href = '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent('/checkout');
    } else {
      window.location.href = '/checkout';
    }
  }

  function bindItemEvents() {
    // Quantity minus
    els.items.querySelectorAll('[data-sc-qty-minus]').forEach(btn => {
      btn.addEventListener('click', () => {
        const item = btn.closest('[data-sc-item]');
        const input = item.querySelector('[data-sc-qty-input]');
        const key = item.dataset.key;
        const newQty = Math.max(0, parseInt(input.value) - 1);
        input.value = newQty;
        
        if (newQty === 0) {
          removeItem(key);
        } else {
          debouncedQuantityChange(key, newQty);
        }
      });
    });

    // Quantity plus
    els.items.querySelectorAll('[data-sc-qty-plus]').forEach(btn => {
      btn.addEventListener('click', () => {
        const item = btn.closest('[data-sc-item]');
        const input = item.querySelector('[data-sc-qty-input]');
        const key = item.dataset.key;

        // Packaging boxes: the bundle sets the count, the customer can't raise it
        if (isPackagingLine(getLineByKey(key))) {
          showToast(PACKAGING_MSG);
          return;
        }

        const newQty = Math.min(99, parseInt(input.value) + 1);
        input.value = newQty;
        debouncedQuantityChange(key, newQty);
      });
    });

    // Quantity direct input
    els.items.querySelectorAll('[data-sc-qty-input]').forEach(input => {
      input.addEventListener('change', () => {
        const item = input.closest('[data-sc-item]');
        const key = item.dataset.key;
        let newQty = Math.max(0, Math.min(99, parseInt(input.value) || 0));

        const capped = capPackagingQty(key, newQty);
        if (capped !== newQty) {
          showToast(PACKAGING_MSG);
          newQty = capped;
        }
        input.value = newQty;

        if (newQty === 0) {
          removeItem(key);
        } else {
          debouncedQuantityChange(key, newQty);
        }
      });
    });

    // Remove
    els.items.querySelectorAll('[data-sc-remove]').forEach(btn => {
      btn.addEventListener('click', () => {
        const item = btn.closest('[data-sc-item]');
        const key = item.dataset.key;
        removeItem(key);
      });
    });

    // Markup rendered by Liquid on page load still has a stepper on packaging
    // lines. Swap it for the fixed readout. JS-rendered lines already have it.
    els.items.querySelectorAll('[data-sc-item]').forEach(itemEl => {
      const line = getLineByKey(itemEl.dataset.key);
      if (!isPackagingLine(line)) return;
      const qtyWrap = itemEl.querySelector('.sc-item__qty');
      if (qtyWrap) qtyWrap.outerHTML = packagingQtyHtml(line.quantity);
    });
  }

  function debouncedQuantityChange(key, qty) {
    clearTimeout(debounceTimers[key]);
    debounceTimers[key] = setTimeout(() => {
      changeQuantity(key, qty);
    }, 350);
  }

  /* ═══════════════════════════════════════════════
     DISCOUNT CODES
     ═══════════════════════════════════════════════ */

  async function applyDiscount() {
    const code = els.discountInput?.value.trim();
    if (!code) return;

    els.discountApply.disabled = true;
    els.discountApply.textContent = '...';

    try {
      // Shopify doesn't have a direct API for discount validation in AJAX.
      // We store the code via the discount URL approach and redirect to checkout.
      // For in-cart display, we use a workaround: set the discount and fetch
      // the cart to see if it applied.
      
      // Method: Use the /discount/ route to set the code
      const res = await fetch(`/discount/${encodeURIComponent(code)}`, {
        method: 'GET',
        redirect: 'manual',
      });

      // Refetch cart to check for discount
      await fetchCart();

      const hasDiscount = cart.cart_level_discount_applications?.length > 0 ||
                         cart.items.some(i => i.discounts?.length > 0);

      if (hasDiscount) {
        showDiscountMessage('Discount applied!', 'success');
        renderCart();
      } else {
        // Code may be valid but applies at checkout — we still save it
        showDiscountMessage('Code saved — will be applied at checkout', 'success');
      }
    } catch (err) {
      showDiscountMessage('Could not apply code', 'error');
    } finally {
      els.discountApply.disabled = false;
      els.discountApply.textContent = 'Apply';
    }
  }

  function showDiscountMessage(msg, type) {
    if (!els.discountMsg) return;
    els.discountMsg.textContent = msg;
    els.discountMsg.className = `sc-discount__msg sc-discount__msg--${type}`;
    setTimeout(() => {
      els.discountMsg.textContent = '';
      els.discountMsg.className = 'sc-discount__msg';
    }, 4000);
  }

  /* ═══════════════════════════════════════════════
     UTILITIES
     ═══════════════════════════════════════════════ */

  /* Formats cents using the store's money format for the customer's currency.
     shop.money_format changes with the market (euro, pounds, krona, francs),
     so every format Shopify can hand us is handled here, matching what
     Liquid's | money filter prints. The old version only knew four of them,
     so a krona format like "{{amount_with_space_separator}} kr" came out raw. */
  function formatMoney(cents) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = Number(cents) || 0;

    const format = CONFIG.moneyFormat || '€{{amount}}';
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;

    function formatWithDelimiters(number, precision, thousands, decimal) {
      const negative = number < 0;
      const fixed = (Math.abs(number) / 100).toFixed(precision);
      const parts = fixed.split('.');
      const whole = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
      const fraction = parts[1] ? decimal + parts[1] : '';
      return (negative ? '-' : '') + whole + fraction;
    }

    const match = format.match(placeholder);
    if (!match) return format;

    let value;
    switch (match[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2, ',', '.');
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', '.');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, '\'', '.');
        break;
      default:
        // Unknown token: show a plain number rather than the raw placeholder
        value = formatWithDelimiters(cents, 2, ',', '.');
    }

    return format.replace(placeholder, value);
  }

  function getSizedImageUrl(url, size) {
    if (!url) return '';
    // Handle Shopify CDN URLs
    if (typeof url === 'object' && url.url) url = url.url;
    if (url.includes('_')) {
      return url;
    }
    const match = url.match(/\.(jpg|jpeg|gif|png|webp|bmp|tiff)(\?.*)?$/i);
    if (!match) return url;
    return url.replace(match[0], `_${size}${match[0]}`);
  }

  function escapeHtml(str) {
    if (!str) return '';
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  /* ── Toast ─────────────────────────────────── */
  let toastEl = null;
  let toastTimeout = null;

  function showToast(message) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'sc-toast';
      document.body.appendChild(toastEl);
    }

    toastEl.textContent = message;
    toastEl.classList.add('sc-toast--visible');

    clearTimeout(toastTimeout);
    toastTimeout = setTimeout(() => {
      toastEl.classList.remove('sc-toast--visible');
    }, 3000);
  }

  /* ── Update header cart bubble count ────────── */
  function updateBubbleCount() {
    // Common selectors for Impulse theme cart count
    const bubbles = document.querySelectorAll(
      '.cart-link__bubble, .cart-count, .cart-link__count, ' +
      '[data-cart-count], .js-cart-count, .header-cart-count, ' +
      '.site-header__cart-count, .cart-icon-bubble'
    );
    bubbles.forEach(el => {
      el.textContent = cart.item_count;
      // Show/hide bubble based on count
      if (cart.item_count > 0) {
        el.removeAttribute('hidden');
        el.classList.remove('hide', 'hidden');
      }
    });
  }

  /* ═══════════════════════════════════════════════
     PUBLIC API
     
     Allow other scripts to interact with the cart:
     
     window.SlideCart.open()
     window.SlideCart.close()
     window.SlideCart.add(variantId, qty)
     window.SlideCart.refresh()
     
     Or via custom events:
     document.dispatchEvent(new CustomEvent('slideCart:requestOpen'))
     ═══════════════════════════════════════════════ */

  window.SlideCart = {
    open: openCart,
    close: closeCart,
    toggle: toggleCart,
    add: addToCart,
    refresh: async () => {
      await fetchCart();
      renderCart();
    },
    getCart: () => cart,
  };

  // Listen for custom events
  document.addEventListener('slideCart:requestOpen', openCart);
  document.addEventListener('slideCart:requestClose', closeCart);
  document.addEventListener('slideCart:requestRefresh', async () => {
    await fetchCart();
    renderCart();
  });

  /* ═══════════════════════════════════════════════
     INIT
     ═══════════════════════════════════════════════ */

  function init() {
    bindGlobalEvents();
    bindItemEvents();
    bindMysteryEvents();

    // Paint the shipping bar and note straight away, so customers paying in
    // another currency see the threshold in their own money from first open
    renderShipping();
    renderTotalsNote();

    // Sync the mystery module with whatever is already in the cart
    renderMystery();

    // On the /cart page, the Remove link reloads the page. If that left a
    // box on its own, take it out and reload so the page shows the real cart.
    if (/\/cart\/?$/.test(window.location.pathname)) {
      removeOrphanPackaging().then(removed => {
        if (removed) window.location.reload();
      });
    }

    // If cart has items, render upsells on load
    if (cart.items.length > 0) {
      fetchAndRenderUpsells();
    }

    // Also intercept Shopify's built-in cart triggers from Impulse
    // Impulse dispatches 'cart:updated' when the cart changes externally
    document.addEventListener('cart:updated', async () => {
      await fetchCart();
      renderCart();
    });

    console.log('[SlideCart] Initialized');
  }

  // Run when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
